import React, { useEffect, useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import EmployeeSelector from '../../EmployeeSelector'

// One "slot" = one Shift document being created. `assignedTo` holds the
// EmployeeSelector option (employee or subcontractor account) and
// `assignedToSubcontractor` flags an external subcontractor with no account.
export const emptySlot = () => ({
  assignedTo: null,
  assignedToSubcontractor: false,
})

export const resizeSlots = (slots, quantity) =>
  Array.from({ length: quantity }, (_, index) => slots[index] ?? emptySlot())

export const isSlotAssigned = (slot) =>
  !!slot?.assignedTo || !!slot?.assignedToSubcontractor

// Shape POSTed to /event-shift as `assignments[i]`
export const slotToPayload = (slot) => ({
  assignedTo: slot?.assignedToSubcontractor
    ? null
    : (slot?.assignedTo?.employee?._id ?? null),
  assignedSubcontractor: slot?.assignedToSubcontractor
    ? null
    : (slot?.assignedTo?.subcontractor?._id ?? null),
  assignedToSubcontractor: !!slot?.assignedToSubcontractor,
})

export const getAssigneeRole = (option) =>
  option?.subcontractor?.clinicalRole ||
  option?.employee?.clinicalRole ||
  option?.clinicalRole ||
  ''

export const getAssigneeName = (option) => {
  if (!option) return ''
  const firstName =
    option.firstName ||
    option.userAccount?.firstName ||
    option.subcontractor?.userAccount?.firstName ||
    option.employee?.userAccount?.firstName ||
    ''
  const lastName =
    option.lastName ||
    option.userAccount?.lastName ||
    option.subcontractor?.userAccount?.lastName ||
    option.employee?.userAccount?.lastName ||
    ''
  return `${firstName} ${lastName}`.trim()
}

const getAssigneePhoto = (option) =>
  option?.photograph ||
  option?.userAccount?.photograph ||
  option?.subcontractor?.userAccount?.photograph ||
  option?.employee?.userAccount?.photograph

const isSubcontractorOption = (option) =>
  !!option?.isSubcontractor || (!!option?.subcontractor && !option?.employee)

// Selector + "covered by a subcontractor" switch for a single slot. Used on
// its own for one shift, and inside each accordion row for several.
export const SlotAssignmentFields = ({
  slot,
  onAssigneeChange,
  onSubcontractorToggle,
  disabled,
  error,
  helperText,
  issues,
  onClearIssues,
  dense,
}) => (
  // Box keeps the Grid gutter intact when rendered inside a Stack
  <Box>
    <Grid container spacing={dense ? 1.5 : 2} alignItems="flex-start">
      <Grid item xs={12} md={dense ? 12 : 7}>
        <EmployeeSelector
          value={slot?.assignedTo ?? null}
          onChange={onAssigneeChange}
          label="Assigned staff"
          size="small"
          fullWidth
          disabled={disabled || !!slot?.assignedToSubcontractor}
          error={error}
          helperText={helperText}
          shrinkLabel={!!slot?.assignedTo}
          includeSubcontractor
        />
      </Grid>
      <Grid item xs={12} md={dense ? 12 : 5}>
        <FormControlLabel
          sx={{ mx: 0, alignItems: 'flex-start' }}
          control={
            <Switch
              checked={!!slot?.assignedToSubcontractor}
              onChange={(e) => onSubcontractorToggle(e.target.checked)}
              disabled={disabled}
              size="small"
            />
          }
          label={
            <Box ml={0.5}>
              <Typography variant="body2" fontWeight={600}>
                Covered by a subcontractor
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For subcontractors without an AmbuNet account
              </Typography>
            </Box>
          }
        />
      </Grid>
      {issues?.length > 0 && (
        <Grid item xs={12}>
          {/* Blockers stop the save; warnings ride along in the same response
              and shouldn't look like the reason it was refused. */}
          <Stack spacing={1}>
            {issues.some((x) => x.severity !== 'warning') && (
              <Alert severity="error" onClose={onClearIssues}>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {issues
                    .filter((x) => x.severity !== 'warning')
                    .map((issue, index) => (
                      <li key={index}>{issue.message}</li>
                    ))}
                </Box>
              </Alert>
            )}
            {issues.some((x) => x.severity === 'warning') && (
              <Alert
                severity="warning"
                onClose={
                  issues.some((x) => x.severity !== 'warning')
                    ? undefined
                    : onClearIssues
                }
              >
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {issues
                    .filter((x) => x.severity === 'warning')
                    .map((issue, index) => (
                      <li key={index}>{issue.message}</li>
                    ))}
                </Box>
              </Alert>
            )}
          </Stack>
        </Grid>
      )}
    </Grid>
  </Box>
)

// Accordion per shift when several are being created at once, so any or all
// of them can be assigned before they're saved.
const ShiftSlotAssignments = ({
  slots,
  onChange,
  disabled,
  issuesBySlot = {},
  onClearIssues,
  onAssign,
}) => {
  const firstOpen = slots.findIndex((slot) => !isSlotAssigned(slot))
  const [expanded, setExpanded] = useState(firstOpen === -1 ? 0 : firstOpen)
  const assignedCount = slots.filter(isSlotAssigned).length
  const errorSlots = Object.keys(issuesBySlot).map(Number)

  // Jump to the first slot the server rejected so the reason is visible
  useEffect(() => {
    if (errorSlots.length) setExpanded(errorSlots[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuesBySlot])

  const updateSlot = (index, patch) => {
    onChange(
      slots.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, ...patch } : slot,
      ),
    )
  }

  const clearAll = () => onChange(slots.map(() => emptySlot()))

  return (
    <Stack spacing={1.25}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={`${assignedCount} of ${slots.length} assigned`}
            color={assignedCount ? 'primary' : 'default'}
            variant={assignedCount ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary">
            {slots.length - assignedCount === 0
              ? 'No open vacancies'
              : `${slots.length - assignedCount} open ${
                  slots.length - assignedCount === 1 ? 'vacancy' : 'vacancies'
                } will be advertised`}
          </Typography>
        </Stack>
        {assignedCount > 0 && !disabled && (
          <Button size="small" color="inherit" onClick={clearAll}>
            Clear all
          </Button>
        )}
      </Stack>

      <Box>
        {slots.map((slot, index) => {
          const assigned = isSlotAssigned(slot)
          const issues = issuesBySlot[index]
          const name = getAssigneeName(slot.assignedTo)
          const role = getAssigneeRole(slot.assignedTo)
          const isSub = isSubcontractorOption(slot.assignedTo)
          const isExpanded = expanded === index
          return (
            <Accordion
              key={index}
              expanded={isExpanded}
              onChange={(e, open) => setExpanded(open ? index : null)}
              disableGutters
              elevation={0}
              // The theme styles Accordions as spaced, shadowed cards; keep
              // the card shape but flatten them into a tight stack
              sx={{
                border: 1,
                borderRadius: 2,
                boxShadow: 'none',
                borderColor: issues?.length
                  ? 'error.main'
                  : isExpanded
                    ? 'primary.main'
                    : 'divider',
                bgcolor: 'background.paper',
                '&:not(:last-child)': { mb: 1 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={{
                  px: 1.5,
                  minHeight: 52,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                    alignItems: 'center',
                    gap: 1.25,
                    minWidth: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: assigned ? 'primary.main' : 'anMuted.main',
                    color: assigned ? 'primary.contrastText' : 'text.secondary',
                  }}
                >
                  {index + 1}
                </Box>
                {slot.assignedToSubcontractor ? (
                  <Avatar
                    sx={{ width: 28, height: 28, bgcolor: 'contrast.main' }}
                  >
                    <HandshakeOutlinedIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                ) : assigned ? (
                  <Avatar
                    src={getAssigneePhoto(slot.assignedTo)}
                    sx={{ width: 28, height: 28 }}
                  >
                    {name.charAt(0)}
                  </Avatar>
                ) : (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: 'transparent',
                      border: '1px dashed',
                      borderColor: 'text.disabled',
                      color: 'text.disabled',
                    }}
                  >
                    <PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                )}
                <Box minWidth={0} flex={1}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {slot.assignedToSubcontractor
                      ? 'External subcontractor'
                      : assigned
                        ? name
                        : 'Open vacancy'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    display="block"
                  >
                    {slot.assignedToSubcontractor
                      ? 'Covered without an AmbuNet account'
                      : assigned
                        ? [role, isSub ? 'Subcontractor' : null]
                            .filter(Boolean)
                            .join(' · ') || 'Assigned'
                        : 'Advertised to staff'}
                  </Typography>
                </Box>
                {issues?.length > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label="Needs attention"
                    sx={{ mr: 1 }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 1.5,
                  pt: 0.5,
                  pb: 1.75,
                  bgcolor: 'background.default',
                }}
              >
                <SlotAssignmentFields
                  slot={slot}
                  dense
                  disabled={disabled}
                  issues={issues}
                  onClearIssues={() => onClearIssues?.(index)}
                  onAssigneeChange={(e, value) => {
                    updateSlot(index, {
                      assignedTo: value ?? null,
                      assignedToSubcontractor: false,
                    })
                    if (value) onAssign?.(value)
                  }}
                  onSubcontractorToggle={(checked) =>
                    updateSlot(index, {
                      assignedToSubcontractor: checked,
                      ...(checked ? { assignedTo: null } : {}),
                    })
                  }
                />
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    </Stack>
  )
}

export default ShiftSlotAssignments
